
import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenAI } from "@google/genai";
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { devError } from '@/utils/devLog';
const env = () => (import.meta as any).env || {};

/** URL du proxy Supabase (Edge Function). Priorité : env build > injection WebView (APK). */
function getChatProxyUrl(): string {
  const url = (env().VITE_SUPABASE_URL || (typeof window !== 'undefined' && (window as any).__SUPABASE_URL__)) as string;
  if (!url || url.includes('placeholder')) return '';
  return `${url.replace(/\/$/, '')}/functions/v1/chat-gemini`;
}

function getSupabaseAnonKey(): string {
  return (env().VITE_SUPABASE_ANON_KEY || (typeof window !== 'undefined' && (window as any).__SUPABASE_ANON_KEY__) || '') as string;
}

function getGeminiApiKey(): string {
  const fromEnv = env().VITE_GEMINI_API_KEY || env().GEMINI_API_KEY;
  const fromWindow = typeof window !== 'undefined' && (window as any).__GEMINI_API_KEY__;
  return (fromEnv || fromWindow || '') as string;
}

/** Vitesse d'affichage typewriter : caractères par intervalle, intervalle en ms */
const TYPEWRITER_CHUNK = 2;
const TYPEWRITER_INTERVAL_MS = 25;

const AIChatAssistant: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{ role: 'user' | 'model'; text: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [typingMessageIndex, setTypingMessageIndex] = useState<number | null>(null);
  const [typingDisplayedLength, setTypingDisplayedLength] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typewriterTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isArabic = i18n.language === 'ar';

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, typingDisplayedLength]);

  // Effet typewriter : affichage progressif du dernier message modèle
  useEffect(() => {
    if (typingMessageIndex === null || typingMessageIndex >= messages.length) return;
    const msg = messages[typingMessageIndex];
    if (msg.role !== 'model') return;
    const fullText = msg.text;
    const len = fullText.length;

    const tick = () => {
      setTypingDisplayedLength((prev) => {
        const next = Math.min(prev + TYPEWRITER_CHUNK, len);
        if (next >= len && typewriterTimerRef.current) {
          clearInterval(typewriterTimerRef.current);
          typewriterTimerRef.current = null;
          setTypingMessageIndex(null);
        }
        return next;
      });
    };

    typewriterTimerRef.current = setInterval(tick, TYPEWRITER_INTERVAL_MS);
    return () => {
      if (typewriterTimerRef.current) {
        clearInterval(typewriterTimerRef.current);
        typewriterTimerRef.current = null;
      }
    };
  }, [typingMessageIndex, messages]);

  const handleResetConversation = () => {
    setMessages([]);
    setInput('');
    setLoading(false);
    setTypingMessageIndex(null);
    setTypingDisplayedLength(0);
    if (typewriterTimerRef.current) {
      clearInterval(typewriterTimerRef.current);
      typewriterTimerRef.current = null;
    }
  };

  const addModelMessageAndStartTyping = (text: string) => {
    setMessages((prev) => {
      const next = [...prev, { role: 'model' as const, text }];
      const newIndex = next.length - 1;
      setTimeout(() => {
        setTypingMessageIndex(newIndex);
        setTypingDisplayedLength(0);
      }, 0);
      return next;
    });
  };

  const buildSystemInstruction = () =>
    `
You are the eServices Assistant for the eServices web application.

Context:
- eServices is a digital platform in Tunisia for mobile top-ups, gaming codes, streaming subscriptions and document services.
- You must ALWAYS answer strictly within the context of the eServices platform.

Knowledge:
- Base your answers on the official content of the eServices site:
  - FAQ: common questions about orders, delays, refunds, payments and account management.
  - Legal pages: terms of use, refund policy, privacy policy and any legal restrictions.
- If a question is covered by these documents, align your answer with them and emphasize important conditions or limitations.

Language:
- Always answer in the user's language: ${i18n.language}.
- If the language is "ar", answer in professional Modern Standard Arabic.
- If the language is "fr", answer in natural professional French.
- If the language is "en", answer in natural professional English.
- Do NOT mix languages in the same answer, except for brand or product names (eServices, Netflix, PUBG, etc.).

Formatting:
- Do NOT use Markdown (no **, no *, no bullet syntax, no headings).
- Do NOT use HTML tags.
- Answer in plain text only, with short paragraphs separated by blank lines.
- For lists, you may start lines with "- " but keep them short and focused.

Behavior:
- Be clear and structured, but avoid unnecessary length.
- Always provide a complete answer that fully addresses the user question, without stopping in the middle of a sentence.
- Limit your answer to a maximum of 3 short paragraphs, or 1 paragraph plus a short list when really needed.
- When the user asks about damaged or defective products, explain the steps briefly: how to contact support, deadlines, and any relevant information from the official documentation (FAQ or legal pages), without adding extra commentary.
- If you are not sure of something or it is not covered by the official documents, say that you are not certain and suggest contacting human support or opening a support ticket.
- Never invent legal information. For legal topics, stay aligned with the official legal pages and mention when the user should carefully read them.

Payments and support:
- If asked about payments, mention that we accept D17, Flouci and Bank Transfer.
- For any technical issue with orders, encourage users to use the "Support Ticket" system inside the application.
`.trim();

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    const fullMessages = [...messages, { role: 'user' as const, text: userMsg }];
    const systemInstruction = buildSystemInstruction();
    const errorFallback = t('AIChatAssistant.messages.errorUnavailable');

    const setError = () => addModelMessageAndStartTyping(errorFallback);

    try {
      const proxyUrl = getChatProxyUrl();
      const anonKey = getSupabaseAnonKey();

      if (proxyUrl && anonKey) {
        try {
          const res = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${anonKey}`,
            },
            body: JSON.stringify({
              messages: fullMessages,
              systemInstruction,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            const text = (data?.text ?? '').trim() || t('AIChatAssistant.messages.errorTechnical');
            addModelMessageAndStartTyping(text);
            setLoading(false);
            return;
          }
          const errBody = await res.text();
          console.warn('[Chat] Proxy non OK:', res.status, errBody);
          let proxyErrorText = errorFallback;
          try {
            const errJson = JSON.parse(errBody);
            if (errJson?.code === 'QUOTA_EXCEEDED') {
              proxyErrorText = t('AIChatAssistant.messages.errorQuota');
            }
          } catch (_) {}
          addModelMessageAndStartTyping(proxyErrorText);
          setLoading(false);
          return;
        } catch (proxyErr) {
          console.warn('[Chat] Erreur proxy (réseau ou CORS):', proxyErr);
        }
      }

      const apiKey = getGeminiApiKey();
      if (!apiKey?.trim()) {
        setError();
        setLoading(false);
        return;
      }
      const ai = new GoogleGenAI({ apiKey });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: fullMessages.map(m => ({ role: m.role, parts: [{ text: m.text }] })),
        config: {
          systemInstruction,
          temperature: 0.3,
          maxOutputTokens: 800,
        },
      });
      const modelText = response.text || t('AIChatAssistant.messages.errorTechnical');
      addModelMessageAndStartTyping(modelText);
    } catch (error) {
      devError('AI Error:', error);
      setError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        'fixed z-[100] transition-all duration-300',
        'bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] md:bottom-8',
        isArabic ? 'left-4 md:left-8' : 'right-4 md:right-8'
      )}
    >
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 w-[320px] sm:w-[380px] h-[500px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-scale-in origin-bottom-right">
          {/* Header */}
          <div className="p-4 bg-primary-500 text-white flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div>
                <p className="text-sm font-bold">{t('AIChatAssistant.header.title')}</p>
                <p className="text-[10px] opacity-80">{t('AIChatAssistant.header.statusOnline')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleResetConversation}
                className="p-1 hover:bg-white/10 rounded"
                aria-label={t('AIChatAssistant.header.resetConversation')}
                title={t('AIChatAssistant.header.resetConversation')}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50 dark:bg-gray-900/20">
            {messages.length === 0 && (
              <div className="text-center py-10 px-6">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-3 text-primary-600">
                   <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                </div>
                <p className="text-sm font-bold text-gray-800 dark:text-white mb-1">
                  {t('AIChatAssistant.emptyState.title')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('AIChatAssistant.emptyState.description')}
                </p>
              </div>
            )}
            {messages.map((msg, i) => {
              const isTypingThis = typingMessageIndex === i && msg.role === 'model';
              const displayText = isTypingThis
                ? msg.text.slice(0, typingDisplayedLength)
                : msg.text;
              return (
                <div key={i} className={clsx("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
                  <div className={clsx(
                    "max-w-[85%] p-3 rounded-2xl text-sm shadow-sm",
                    msg.role === 'user' 
                      ? "bg-primary-500 text-white rounded-tr-none" 
                      : "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-600 rounded-tl-none"
                  )}>
                    {displayText}
                    {isTypingThis && typingDisplayedLength < msg.text.length && (
                      <span className="inline-block w-0.5 h-4 ml-0.5 align-middle bg-primary-500 animate-pulse" aria-hidden />
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-700 p-3 rounded-2xl rounded-tl-none border border-gray-100 dark:border-gray-600 flex gap-1">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0s]"></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex gap-2">
              <input 
                type="text" 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('AIChatAssistant.input.placeholder')}
                className="flex-1 bg-gray-100 dark:bg-gray-700 border-none rounded-full px-4 py-2 text-sm focus:ring-2 focus:ring-primary-500 dark:text-white"
              />
              <button type="submit" disabled={!input.trim() || loading} className="p-2 bg-primary-500 text-white rounded-full hover:bg-primary-400 disabled:opacity-50 transition-colors">
                <svg className={clsx("w-5 h-5", isArabic && "rotate-180")} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 bg-primary-500 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 relative group"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        ) : (
          <div className="relative">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 border-2 border-primary-600 rounded-full"></span>
          </div>
        )}
        
        {/* Tooltip: au-dessus centré sur mobile; à côté du bouton (caché par le chat) sur desktop — LTR à gauche, RTL à droite */}
        {!isOpen && (
          <div
            className={clsx(
              "absolute whitespace-nowrap bg-gray-900 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none",
              "bottom-full left-1/2 -translate-x-1/2 mb-2",
              "md:bottom-auto md:mb-0 md:top-1/2 md:-translate-y-1/2",
              isArabic ? "md:left-full md:right-auto md:translate-x-0 md:ml-2" : "md:left-auto md:right-full md:translate-x-0 md:mr-2"
            )}
          >
            {t('AIChatAssistant.tooltip.help')}
          </div>
        )}
      </button>

      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-scale-in {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default AIChatAssistant;
